import { DateTime } from 'luxon';

import {
  type ComparisonRange,
  PRIMARY_RANGE_ID,
  type ReportConfiguration,
} from '../report-configurations/reportConfigurationsTypes';
import {
  type ResolvedPrimaryRangeDates,
  type ResolvedRangeDates,
  resolveComparisonPrimaryRangeDates,
  resolveComparisonRangeDates,
} from '../report-configurations/utils/comparisonRanges';
import type { ComparisonColumnGroupType, Report, ReportCell, ReportColumn, ReportRow } from './generatedReportsTypes';

// ================================================================================================
// Comparison column model
//
// The single place that understands the grouped-column shape of a COMPARISON report. Consumers
// should ask this module rather than reading `columnGroupId` / `comparisonColumnType` ad hoc, so
// the grouping rules live in one testable place.
//
// A comparison report's columns are a flat list carrying group metadata:
//
//   [ Period 1: PRIMARY VALUE, PRIOR_YEAR VALUE, PRIMARY CUMULATIVE, PRIOR_YEAR DELTA_AMOUNT, ...
//     Period 2: ...
//     Total:    PRIMARY VALUE, PRIOR_YEAR VALUE, ... ]
//
// Two rules worth stating up front, because getting either wrong is silent:
//   1. Group by `columnGroupId`, classify by `columnGroupType`. `columnGroupLabel` is a
//      server-supplied English display string ("Period 1", "Total") — not a stable key.
//   2. The TOTAL group has N columns, one per range plus derived ones. Any "the last column is the
//      total" assumption (`cols.at(-1)?.isAggSum`) is wrong here.
// ================================================================================================

export type ComparisonColumnGroup = {
  groupId: string;
  /** Display string from the server. Render it; do not branch on it. */
  label?: string;
  type?: ComparisonColumnGroupType;
  columns: ReportColumn[];
};

export type ComparisonColumnSource = 'PERIOD' | 'TOTAL';

export const DEFAULT_COMPARISON_COLUMN_SOURCE: ComparisonColumnSource = 'PERIOD';

/**
 * Whether a generated report is a multi-range comparison report.
 *
 * Reads the request configuration rather than `report.reportType`. The response field is
 * defaulted to `'TRANSACTION'` in `transformResponseToReport` when QCS omits it, which would make
 * a comparison report claim to be a transaction report — and the transaction-only guards in the
 * pie-chart selectors would then process it instead of returning early.
 */
export const isComparisonReport = (report: Report | null | undefined): boolean =>
  report?.configuration?.reportType === 'COMPARISON' || report?.reportType === 'COMPARISON';

/**
 * Drill-down `items` are only ever populated on VALUE cells. A derived column has no underlying
 * transactions of its own, so offering drill-down on one would open an empty or misleading register.
 */
export const isDrillDownableColumn = (column: ReportColumn | null | undefined): boolean =>
  column?.comparisonColumnType === 'VALUE';

export const isPrimaryRangeColumn = (column: ReportColumn | null | undefined): boolean =>
  column?.rangeId === PRIMARY_RANGE_ID;

/**
 * Group a comparison report's columns, preserving the server's column order both between groups
 * and within each group. Column order is contract — it is what the configured range order and
 * `additionalColumnTypes` order produce — so this must never sort.
 *
 * Columns with no `columnGroupId` are skipped rather than bucketed under a synthetic group; on a
 * non-comparison report that means the result is simply empty.
 */
export const getColumnGroups = (report: Report | null | undefined): ComparisonColumnGroup[] => {
  const groups: ComparisonColumnGroup[] = [];
  const byId = new Map<string, ComparisonColumnGroup>();

  (report?.columns ?? []).forEach((column) => {
    const { columnGroupId } = column;
    if (!columnGroupId) {
      return;
    }

    let group = byId.get(columnGroupId);
    if (!group) {
      // First sighting fixes the group's position, so groups come back in server order.
      group = { groupId: columnGroupId, label: column.columnGroupLabel, type: column.columnGroupType, columns: [] };
      byId.set(columnGroupId, group);
      groups.push(group);
    }
    group.columns.push(column);
  });

  return groups;
};

/**
 * Resolve the cell a column addresses on a given row.
 *
 * An ordinary report's column ids are positional ("0", "1", …) and every row carries one cell per
 * column in order, so consumers have always indexed `cellData` by the id coerced to a number. That
 * is actively wrong for a comparison report: ids are opaque strings, `cellData` need not follow
 * column order, and a range that has already ended omits its cell entirely.
 *
 * The failure is silent rather than loud. `Number('p1-primary-value')` is `NaN`, and
 * `Array.prototype.at(NaN)` coerces the index to 0 — so an unguarded ordinal lookup returns the
 * *first* period's cell for every column the user drills into, rather than returning nothing.
 */
export const findCellForColumn = (
  row: ReportRow | null | undefined,
  columnId: string | number | null | undefined,
  isComparison: boolean,
): ReportCell | undefined => {
  if (columnId == null) return undefined;
  if (isComparison) {
    return row?.cellData?.find((cell) => cell.columnId === String(columnId));
  }
  const ordinal = Number(columnId);
  if (!Number.isInteger(ordinal)) return undefined;
  return row?.cellData?.at(ordinal);
};

/**
 * The column ids that make up one comparison "bar pair": every range's column inside the group the
 * given column belongs to.
 *
 * A bar and its hatched counterpart are one unit in the chart — the whole point of the row is the
 * comparison — so drilling into either should open the transactions behind both. Derived columns
 * are swept in only when the payload leaves `comparisonColumnType` unpopulated (QCS-25062); they
 * carry no `items` of their own, so including them adds nothing rather than adding the wrong thing.
 *
 * Falls back to the column itself, so a non-comparison report needs no branch of its own.
 */
export const getPairedColumnIds = (
  report: Report | null | undefined,
  columnId: string | number | null | undefined,
): string[] => {
  if (columnId == null) return [];
  const id = String(columnId);
  if (!isComparisonReport(report)) return [id];

  const columns = report?.columns ?? [];
  const clicked = columns.find((column) => String(column.id) === id);
  if (!clicked?.columnGroupId) return [id];

  const paired = columns
    .filter(
      (column) =>
        column.columnGroupId === clicked.columnGroupId &&
        (column.comparisonColumnType == null || column.comparisonColumnType === 'VALUE'),
    )
    .map((column) => String(column.id));

  return paired.length ? paired : [id];
};

export const getPeriodColumnGroups = (report: Report | null | undefined): ComparisonColumnGroup[] =>
  getColumnGroups(report).filter((group) => group.type === 'PERIOD');

/**
 * The whole-range total group. Prefer this over `Report.total`, which is a single scalar and
 * reflects the primary range only.
 */
export const getTotalColumnGroup = (report: Report | null | undefined): ComparisonColumnGroup | undefined =>
  getColumnGroups(report).find((group) => group.type === 'TOTAL');

/** Every group claiming to be the whole-range total. More than one is a malformed response. */
export const getTotalColumnGroups = (report: Report | null | undefined): ComparisonColumnGroup[] =>
  getColumnGroups(report).filter((group) => group.type === 'TOTAL');

/** Typed column groups selected for a comparison presentation, in server order. */
export const getComparisonColumnGroupsForSource = (
  report: Report | null | undefined,
  comparisonColumnSource: ComparisonColumnSource,
): ComparisonColumnGroup[] =>
  comparisonColumnSource === 'PERIOD' ? getPeriodColumnGroups(report) : getTotalColumnGroups(report).slice(0, 1);

export type ComparisonPeriodGridVisibility = Map<string, Map<string, boolean>>;

const getValidRangeDates = (range: ResolvedRangeDates | undefined): ResolvedRangeDates | undefined => {
  if (
    !range ||
    !DateTime.fromISO(range.startOn).isValid ||
    !DateTime.fromISO(range.endOn).isValid ||
    range.startOn > range.endOn
  ) {
    return undefined;
  }
  return range;
};

/** Inclusive overlap of a server period and one selected range. Unknown dates remain visible. */
const overlapsSelectedRange = (column: ReportColumn, selectedRange: ResolvedRangeDates | undefined): boolean => {
  if (!selectedRange || column.headerType !== 'DATE_RANGE') return true;
  const bucket = getValidRangeDates(column.headerValue);
  if (!bucket) return true;
  return bucket.startOn <= selectedRange.endOn && bucket.endOn >= selectedRange.startOn;
};

const resolveSelectedComparisonRangeDates = (
  primary: ResolvedPrimaryRangeDates | undefined,
  range: ComparisonRange,
  configuration: ReportConfiguration,
  currentDate: DateTime,
): ResolvedRangeDates | undefined => {
  const resolved = resolveComparisonRangeDates(primary, range);
  if (resolved) return resolved;

  if (range.dateRange === 'PRIOR_PERIOD' || range.dateRange === 'PRIOR_YEAR_PERIOD') {
    return undefined;
  }

  return resolveComparisonPrimaryRangeDates(
    configuration.merge({
      dateRange: range.dateRange,
      startOn: range.startOn,
      endOn: range.endOn,
      dateRangeInterval: range.dateRangeInterval,
      dateRangeIntervalCount: range.dateRangeIntervalCount,
    }),
    currentDate,
  );
};

/**
 * Visibility for each range's VALUE column in each period. The first VALUE group owns a period;
 * QCS may place its derived columns in following groups with different IDs.
 */
export const getComparisonPeriodGridVisibility = (
  report: Report | null | undefined,
  currentDate: DateTime = DateTime.local(),
): ComparisonPeriodGridVisibility => {
  const visibility: ComparisonPeriodGridVisibility = new Map();
  if (!report || !isComparisonReport(report)) return visibility;

  const primary = resolveComparisonPrimaryRangeDates(report.configuration, currentDate);
  const selectedRanges = new Map<string, ResolvedRangeDates | undefined>([
    [PRIMARY_RANGE_ID, getValidRangeDates(primary)],
    ...(report.configuration.comparisonRanges ?? []).map((range) => {
      const dates = resolveSelectedComparisonRangeDates(primary, range, report.configuration, currentDate);
      return [range.rangeId, getValidRangeDates(dates)] as const;
    }),
  ]);

  getPeriodColumnGroups(report).forEach((group) => {
    const valueColumns = group.columns.filter(isChartableComparisonColumn);
    if (!valueColumns.length) return;
    const ranges = new Map<string, boolean>();
    valueColumns.forEach((column) => {
      const rangeId = column.rangeId ?? PRIMARY_RANGE_ID;
      ranges.set(rangeId, overlapsSelectedRange(column, selectedRanges.get(rangeId)));
    });
    visibility.set(group.groupId, ranges);
  });

  return visibility;
};

export type GetGridColumnsOptions = {
  comparisonColumnSource?: ComparisonColumnSource;
  /** Restrict the period view to the period currently opened from the over-time chart. */
  comparisonPeriodGroupId?: string;
  /**
   * Compatibility mapping for consumers that have not adopted `comparisonColumnSource` yet.
   * An explicit source takes precedence.
   */
  showPeriods?: boolean;
};

/**
 * The columns a comparison grid renders — one group type at a time, never both.
 *
 * The two sources are mutually exclusive by design: TOTAL shows the whole-range group and nothing
 * else; PERIOD shows period groups and drops Total, because a Total beside per-period columns reads
 * as another period rather than as their sum.
 *
 * This is a *view* filter and belongs to grid column construction alone. The groups it leaves out
 * are not unwanted data, so nothing upstream of the grid (transformers or response model) may
 * apply it. Hidden is not discarded.
 *
 * Lives in core rather than the web layer because "what a comparison grid shows" is one definition
 * shared with mobile, not a platform rendering detail.
 */
export const getGridColumns = (
  report: Report | null | undefined,
  options: GetGridColumnsOptions = {},
): ReportColumn[] => {
  // Returned by reference so a non-comparison caller needs no branch of its own.
  if (!isComparisonReport(report)) {
    return report?.columns ?? [];
  }

  // A response with no typed groups at all is not a grouped comparison payload — an older service,
  // a flag mismatch, or a shape this client does not understand. There is nothing to filter *by*,
  // so render it flat rather than filtering everything away: a grid showing more columns than
  // intended is a far better failure than a report that reports itself empty while holding data.
  // A typed response that omits the selected source returns nothing, allowing the caller to show
  // an empty state rather than a misleading partial layout.
  const groups = getColumnGroups(report);
  if (!groups.some((group) => group.type)) {
    return report?.columns ?? [];
  }

  const comparisonColumnSource =
    options.comparisonColumnSource ??
    (options.showPeriods == null ? DEFAULT_COMPARISON_COLUMN_SOURCE : options.showPeriods ? 'PERIOD' : 'TOTAL');

  const sourceGroups = getComparisonColumnGroupsForSource(report, comparisonColumnSource);
  const selectedPeriodIndex =
    comparisonColumnSource === 'PERIOD' && options.comparisonPeriodGroupId
      ? sourceGroups.findIndex((group) => group.groupId === options.comparisonPeriodGroupId)
      : -1;
  // QCS can give derived columns their own group IDs. They still belong to the preceding period's
  // value group, so keep them when the grid opens one period from the chart. Older responses omit
  // comparisonColumnType, so those value groups also mark the start of the next period.
  const nextValueGroupIndex = sourceGroups.findIndex(
    (group, index) => index > selectedPeriodIndex && group.columns.some(isChartableComparisonColumn),
  );
  const requestedPeriodGroups =
    selectedPeriodIndex >= 0
      ? sourceGroups.slice(selectedPeriodIndex, nextValueGroupIndex < 0 ? undefined : nextValueGroupIndex)
      : undefined;
  // A selected period id is transient UI state. If regeneration replaces the server's group ids,
  // prefer the complete period grid over a one-render empty flash while that state reconciles.
  const visibleGroups = requestedPeriodGroups?.length ? requestedPeriodGroups : sourceGroups;

  if (comparisonColumnSource === 'TOTAL') {
    return visibleGroups
      .flatMap((group) => group.columns)
      .filter((column) => column.comparisonColumnType !== 'CUMULATIVE_VALUE');
  }

  const periodVisibility = getComparisonPeriodGridVisibility(report);
  let currentRanges: Map<string, boolean> | undefined;
  return visibleGroups.flatMap((group) => {
    if (group.columns.some(isChartableComparisonColumn)) currentRanges = periodVisibility.get(group.groupId);
    return group.columns.filter((column) => {
      if (column.comparisonColumnType === 'CUMULATIVE_VALUE') return false;
      if (!currentRanges) return true;
      const rangeId = column.rangeId ?? PRIMARY_RANGE_ID;
      if (isChartableComparisonColumn(column)) return currentRanges.get(rangeId) !== false;
      return currentRanges.get(PRIMARY_RANGE_ID) === true && currentRanges.get(rangeId) === true;
    });
  });
};

/**
 * Whether a column holds a range's raw value.
 *
 * Deliberately laxer than a strict `'VALUE'` check. A nullish `comparisonColumnType` means "the server did
 * not say" rather than "derived", and a payload that leaves it unpopulated would otherwise classify
 * every column of every report as derived — so a nullish type is treated as a value column.
 */
export const isChartableComparisonColumn = (column: ReportColumn): boolean =>
  column.comparisonColumnType == null || column.comparisonColumnType === 'VALUE';

/** A range's display name and the dates it covers, gathered from the columns that carry them. */
export type ComparisonRangeHeader = { label?: string; startOn?: string; endOn?: string; periodGroupIds: Set<string> };

/**
 * What each range is called and when it ran, read from the **PERIOD** columns.
 *
 * A TOTAL-group column is headed `STRING`/"Total" — the same string on every column in the group —
 * so it names the *group*, not the range. Only the PERIOD columns carry a `DATE_RANGE` header, and
 * that is where both the display name and the range's own dates live. Reading them from there is
 * what stops every TOTAL column from being labelled "Total" and what gives a drill-down the range
 * it was launched from rather than the report's primary dates (QCS-25115).
 *
 * Keyed by `rangeId`, which is the one field a TOTAL column and its PERIOD columns share.
 *
 * `periodGroupIds` counts the *groups* a range spans rather than the columns, because a period group
 * also holds the columns derived from the range (`DELTA_AMOUNT` carries the same `rangeId` and the
 * same dates). It decides whether one period's name can stand for the whole range: with a single
 * period — every comparison report today, since `columnType` is pinned to `NONE` — the name covers
 * exactly the span the TOTAL column does. Across several it would name only the first, so the
 * caller falls back to `rangeLabel` and the dates widen to the union.
 */
export const getComparisonRangeHeaders = (report: Report | null | undefined): Map<string, ComparisonRangeHeader> => {
  const headers = new Map<string, ComparisonRangeHeader>();

  getPeriodColumnGroups(report).forEach((group) => {
    group.columns.filter(isChartableComparisonColumn).forEach((column) => {
      if (column.headerType !== 'DATE_RANGE') return;

      const rangeId = column.rangeId ?? PRIMARY_RANGE_ID;
      const { startOn, endOn, name } = column.headerValue;
      const header = headers.get(rangeId);
      if (!header) {
        headers.set(rangeId, {
          label: name,
          startOn,
          endOn,
          periodGroupIds: new Set(group.groupId ? [group.groupId] : []),
        });
        return;
      }

      // ISO dates, so string comparison is date comparison. Widen to the union of the periods.
      if (startOn && (!header.startOn || startOn < header.startOn)) header.startOn = startOn;
      if (endOn && (!header.endOn || endOn > header.endOn)) header.endOn = endOn;
      if (group.groupId) header.periodGroupIds.add(group.groupId);
    });
  });

  return headers;
};

/**
 * The label and dates to show for one comparison column, joining it to its range's PERIOD columns.
 *
 * The server names the period each PERIOD column covers, so the label is read rather than
 * reconstructed — but it has to be read from a column that has a name. The column's own header
 * comes first when it is a date range; then the period's name, the only source that describes the
 * span a TOTAL column actually covers; then `rangeLabel`, which is a fallback rather than the answer
 * because the server sends the literal "Primary" for the primary range — an echo of `rangeId`, not
 * something to show a person. `label` is left empty rather than invented when nothing resolves, so
 * the caller decides what an unresolvable header reads as.
 *
 * `source` says which of those the label came from, so a caller that has a better fallback than
 * `rangeLabel` for its own surface can tell a real range name from the echo of a `rangeId`.
 *
 * Pass the map from `getComparisonRangeHeaders` so a caller resolving several columns builds it once.
 */
export const resolveComparisonRangeHeader = (
  column: ReportColumn,
  headers: Map<string, ComparisonRangeHeader>,
): { label: string; source: 'own' | 'period' | 'rangeLabel' | 'none'; startOn?: string; endOn?: string } => {
  const own = column.headerType === 'DATE_RANGE' ? column.headerValue : undefined;
  const header = headers.get(column.rangeId ?? PRIMARY_RANGE_ID);
  const spansOnePeriod = (header?.periodGroupIds.size ?? 0) <= 1;
  const periodLabel = spansOnePeriod ? header?.label : undefined;
  const label = own?.name || periodLabel || column.rangeLabel || '';

  let source: 'own' | 'period' | 'rangeLabel' | 'none' = 'none';
  if (own?.name) source = 'own';
  else if (periodLabel) source = 'period';
  else if (column.rangeLabel) source = 'rangeLabel';

  return {
    label,
    source,
    startOn: own?.startOn ?? header?.startOn,
    endOn: own?.endOn ?? header?.endOn,
  };
};
